import {
  createZzkTheme,
  createZzkThemeCssVariables,
  createZzkThemeCssVariablesDark,
} from "@zzk/ui";
import { utcNowIso } from "@zzk/core";
import { brandAssets, type BrandAssets } from "./assets.js";

export interface AppBrandConfig {
  readonly id: 'default';
  readonly name: string;
  readonly shortName: string;
  readonly assets: BrandAssets;
  readonly theme: ReturnType<typeof createZzkTheme>;
  readonly themeCss: string;
  readonly source: 'static' | 'runtime';
  readonly updatedAt: string;
}

export const appTheme = createZzkTheme({
  name: "zzk-lens",
  density: "comfortable",
  radius: 6,
});

/**
 * Do NOT replace with createZzkThemeCssText: that emits an
 * `@media (prefers-color-scheme: dark)` block that forces dark tokens under OS dark mode,
 * overriding our light default and breaking /app/login contrast. Dark applies only via
 * an explicit `data-theme="dark"` attribute.
 */
function buildAppThemeCss(theme: ReturnType<typeof createZzkTheme>): string {
  const toDeclarations = (vars: Record<string, string>): string =>
    Object.entries(vars)
      .map(([name, value]) => `  ${name}: ${value};`)
      .join("\n");

  const lightBlock = `:root {\n${toDeclarations(createZzkThemeCssVariables(theme))}\n}`;
  const darkBlock = `:root[data-theme="dark"] {\n${toDeclarations(
    createZzkThemeCssVariablesDark(theme),
  )}\n}`;

  return `${lightBlock}\n\n${darkBlock}`;
}

export const appThemeCss = buildAppThemeCss(appTheme);

export const brandConfig: AppBrandConfig = {
  id: 'default',
  name: "Zzk Lens",
  shortName: "zzk-lens",
  assets: brandAssets,
  theme: appTheme,
  themeCss: appThemeCss,
  source: 'static',
  updatedAt: utcNowIso(),
};
